/**
 * navbar.js - the catalog-rendered CAS navbar.
 *
 * Loaded by the SPA shell (web_bs/static/index.htmlp) as a module. Renders the
 * whole navbar (menus, search palette trigger, account menu, mobile offcanvas
 * drawer) into the static brand shell from the same build-generated launch
 * catalog the homepage launchpad uses (#cas-launch-catalog), access-filtered
 * client-side with the shared homepage_utils/launch_access predicates. The
 * old second-level flyout submenus render as titled sections inside their
 * group's dropdown panel.
 *
 * Handoff with cas.js (which is a plain script, not a module):
 *   - cas.js dispatches "cas:security-updated" on document (detail:
 *     { securityParams, webApps }) whenever get_security_params /
 *     get_application_status complete (initial load, after logout). The navbar
 *     re-renders from that payload. Until the first event it shows a minimal
 *     logged-out shell (Home + Sign in).
 *   - after every render this module dispatches "cas:navbar-rendered";
 *     cas.js (re)binds its jQuery handlers on #lLogin / #lLogout there. The
 *     ids lLogin / lLogout / lAccountSettings / accountmenu /
 *     account-menu-dropdown are load-bearing: cas.js force-clicks #lLogout on
 *     session timeouts and noVNC.js unbinds it, so they must stay, jQuery-bound.
 *
 * Keyboard:
 *   - Ctrl+K / Cmd+K opens the command palette (search over the catalog with
 *     the user's pinned favorites and recent launches in the empty state).
 *   - With UseKeyboardShortcuts on, the old per-level shortcut letters keep
 *     working through navbar_utils/shortcut_nav: a group letter opens its
 *     panel, a section letter arms (highlights) the section, an item letter
 *     launches. Escape pops one level.
 */
import cdom from "../static/3rdparty/cdom/cdom.min.js";
import { icon } from "./svg_icons.js";
import { GetElById } from "./general_utils.js";
import * as userState from "./user_state.js";
import {
  accessibleTiles,
  guestTiles,
  tilesByCommand,
  tilesByLaunchKey,
  launchKey,
} from "./homepage_utils/launch_access.js";
import {
  getRecentLaunchEntries,
  normalizeRecentsState,
} from "./homepage_utils/launch_recents.js";
import {
  PINNED_LAUNCH_EVENT,
  togglePinned,
  isPinned,
  getPinnedKeys,
  migratePinnedEntries,
} from "./homepage_utils/launch_pins.js";
import { formatRelativeTime } from "./homepage_utils/relative_time.js";
import {
  roleLabel,
  quickLaunchTiles,
  quickActionCommandsForRole,
} from "./homepage_utils/command_center.js";
import {
  buildMenuTree,
  panelColumns,
  activeCommandFromHash,
  markShortcut,
} from "./navbar_utils/menu_tree.js";
import { paletteResults, flattenResults, moveSelection } from "./navbar_utils/palette_model.js";
import { step, IDLE_STATE } from "./navbar_utils/shortcut_nav.js";
import { helpText } from "./help_hint.js";

const { div, span, button, a, ul, li, input, kbd } = cdom.elements;

/** Per-category icon (keyed by the stable groupKey).
 *  Keep in sync with GROUP_ICON in homepage.js. */
const GROUP_ICON = {
  NEWAD: "tag",
  INQUIRE: "search",
  MANAGE: "user",
  APPROVE: "clipboardCheck",
  SETTINGS: "slidersHorizontal",
  CUSTOMIZE: "settings",
  "": "layoutGrid",
};

/** The homepage is excluded from the catalog (it IS the launchpad), but the
 *  navbar still wants a first-class Home link, so it synthesizes one. */
const HOME_TILE = {
  command: "homepage",
  label: "Home",
  shortcut: "H",
  href: "/scs_bs/#homepage",
  groupKey: "",
  group: "",
  desc: "",
  bootstrap: true,
};

/** Mutable navbar state, rebuilt on every cas:security-updated. */
const NAVBAR = {
  catalog: [],
  ctx: null, // null until the first security event: minimal logged-out shell
  tiles: [],
  tree: [],
  byCommand: new Map(),
  topNodes: [], // [{ el, commands: Set }] for active-page marking
  shortcutState: IDLE_STATE,
  palette: null, // { query, selection, model } while open
  ocQuery: "",
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** Reads the build-generated catalog embedded in the page. [] if absent. */
function readLaunchCatalog() {
  const el = document.getElementById("cas-launch-catalog");
  if (!el) return [];
  try {
    const data = JSON.parse(el.textContent || "[]");
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}

// Pins and recents live in the per-login server-side state (user_state.js).
// The navbar renders on cas:security-updated, which can precede that state's
// arrival, so the bootstrap below re-reads once userState.ready() resolves.
function loadRecentsState() {
  return (
    userState.read(userState.KEYS.LAUNCHPAD_RECENTS, normalizeRecentsState) || {
      version: 1,
      entries: [],
    }
  );
}

function loadPinsState() {
  return (
    userState.read(userState.KEYS.LAUNCHPAD_PINS, (v) =>
      v && Array.isArray(v.entries) ? { version: v.version || 1, entries: v.entries } : null,
    ) || { version: 1, entries: [] }
  );
}

function savePinsState(state) {
  userState.set(userState.KEYS.LAUNCHPAD_PINS, state);
}

/** Absorbs a cas:security-updated payload into the render context. */
function setContext(detail) {
  const sp = (detail && detail.securityParams) || {};
  const level = Number(sp.AdminLevel);
  const loggedIn = !sp.error && level >= 0 && level <= 4;
  NAVBAR.ctx = {
    loggedIn,
    adminLevel: level,
    webSecurity: sp.WebSecurity || {},
    webApps: (detail && detail.webApps) || [],
    fullName: sp.FullUserName || sp.UserName || "",
    useKeyboardShortcuts: !!sp.UseKeyboardShortcuts,
  };
}

/** Recomputes tiles / tree / command map from the current context. */
function rebuild() {
  const ctx = NAVBAR.ctx;
  if (!ctx) {
    // Pre-event shell: no catalog items (the old navbar was likewise empty
    // until the security round trip finished), just Home + Sign in.
    NAVBAR.tiles = [];
    NAVBAR.tree = [{ type: "link", tile: HOME_TILE }];
  } else {
    NAVBAR.tiles = ctx.loggedIn
      ? accessibleTiles(NAVBAR.catalog, ctx)
      : guestTiles(NAVBAR.catalog, ctx.webApps);
    NAVBAR.tree = [{ type: "link", tile: HOME_TILE }, ...buildMenuTree(NAVBAR.catalog, ctx)];
  }
  NAVBAR.byCommand = tilesByCommand(NAVBAR.tiles);
  NAVBAR.byLaunchKey = tilesByLaunchKey(NAVBAR.tiles);
  NAVBAR.shortcutState = IDLE_STATE;
}

/**
 * Pins persisted before the launchKey change are bare command names; rewrite
 * them once against the FULL catalog. The navbar mounts on every SPA page, so
 * this runs even when the user never lands on the homepage (which migrates
 * the same way). The result is written to the per-login server state, so it
 * is resolved against the catalog and not the accessible tiles: those depend
 * on webApps, which reads as empty on a failed fetch, and migrating against
 * that would drop every bare pin the user holds. Guarded to a logged-in
 * context and a non-empty catalog (migratePinnedEntries also returns the same
 * reference against an empty one) so nothing here can wipe stored pins, and
 * written only when the migration changed something. Runs after
 * userState.ready(), never from rebuild(): before the state has loaded the
 * pin list reads as empty and there is nothing to migrate.
 */
function migrateStoredPins() {
  const ctx = NAVBAR.ctx;
  if (!(ctx && ctx.loggedIn && NAVBAR.catalog.length)) return;
  const pins = loadPinsState();
  const migrated = migratePinnedEntries(pins, NAVBAR.catalog);
  if (migrated !== pins) savePinsState(migrated);
}

/** Repaints the surfaces built from pins/recents once the user state has loaded. */
function onUserStateReady() {
  migrateStoredPins();
  const el = GetElById("casNavOcResults");
  if (el) cdom.replaceInner(el, renderOffcanvasContent);
  if (NAVBAR.palette) rerenderPaletteResults();
}

// ---------------------------------------------------------------------------
// Render: the bar
// ---------------------------------------------------------------------------

function render() {
  rebuild();
  const mount = GetElById("cas-navbar-mount");
  if (!mount) return;
  NAVBAR.topNodes = [];
  cdom.replaceInner(mount, () => {
    renderNavList();
    renderSearchButton();
    renderAccountMenu();
  });
  renderOffcanvas();
  markActive();
  document.dispatchEvent(new CustomEvent("cas:navbar-rendered"));
}

function renderNavList() {
  ul({ class: "navbar-nav cas-nav-list" }, () => {
    for (const node of NAVBAR.tree) {
      if (node.type === "link") renderTopLink(node.tile);
      else renderGroup(node);
    }
  });
}

function renderTopLink(tile) {
  li({ class: "nav-item" }, () => {
    const el = a(
      {
        class: "nav-link cas-nav-toplink",
        href: tile.href,
        "data-shortcut": tile.shortcut || "",
        "data-command": tile.command,
      },
      () => {
        if (tile.command === "homepage") {
          span({ class: "cas-nav-topicon" }, () => icon("home"));
        }
        renderShortcutLabel(tile.label, tile.shortcut, "cas-nav-toplabel");
      },
    );
    NAVBAR.topNodes.push({ el, commands: new Set([tile.command]) });
  });
}

function renderGroup(group) {
  const commands = new Set();
  group.items.forEach((t) => commands.add(t.command));
  group.sections.forEach((s) => s.items.forEach((t) => commands.add(t.command)));

  li({ class: "nav-item dropdown cas-nav-group", "data-group-key": group.key }, () => {
    const btn = button(
      {
        type: "button",
        class: "nav-link dropdown-toggle cas-nav-toplink",
        "data-bs-toggle": "dropdown",
        "data-shortcut": group.shortcut || "",
        "aria-expanded": "false",
      },
      () => {
        renderShortcutLabel(group.label, group.shortcut, "cas-nav-toplabel");
      },
    );
    NAVBAR.topNodes.push({ el: btn, commands });

    // Big menus lay out as side-by-side columns (a mega panel) so everything
    // is visible at once; small ones stay a simple list.
    const columns = panelColumns(group);
    const mega = columns.length > 1;
    div({ class: "dropdown-menu cas-nav-panel" + (mega ? " cas-nav-panel--mega" : "") }, () => {
      columns.forEach((column) => {
        const renderColumn = () => column.forEach(renderPanelSegment);
        if (mega) div({ class: "cas-nav-panel-col" }, renderColumn);
        else renderColumn();
      });
    });
  });
}

/** One panelColumns segment: a titled section, a continuation chunk of one,
 *  or the group's headerless loose items. */
function renderPanelSegment(seg) {
  if (!seg.key && !seg.label) {
    seg.items.forEach(renderPanelItem);
    return;
  }
  const cls = "cas-nav-section" + (seg.continuation ? " cas-nav-section--cont" : "");
  div({ class: cls, "data-section-key": seg.key }, () => {
    if (seg.label) {
      div({ class: "cas-nav-section-title", "data-shortcut": seg.shortcut || "" }, () =>
        renderShortcutLabel(seg.label, seg.shortcut),
      );
    }
    seg.items.forEach(renderPanelItem);
  });
}

function renderPanelItem(tile) {
  a(
    {
      class: "dropdown-item cas-nav-item",
      href: tile.href,
      "data-shortcut": tile.shortcut || "",
      "data-command": tile.command,
      title: tile.desc || tile.label,
    },
    () => {
      span({ class: "cas-nav-item-text" }, () => {
        renderShortcutLabel(tile.label, tile.shortcut, "cas-nav-item-label");
        if (tile.desc) span({ class: "cas-nav-item-desc" }, tile.desc);
      });
    },
  );
}

/** Label with the keyboard-shortcut letter underlined in place (only when the
 *  user has shortcuts enabled, matching the old navbar). */
function renderShortcutLabel(label, shortcut, cls) {
  const marked =
    NAVBAR.ctx && NAVBAR.ctx.useKeyboardShortcuts ? markShortcut(label, shortcut) : null;
  span({ class: cls || "" }, () => {
    if (!marked) {
      span(label);
      return;
    }
    if (marked.pre) span(marked.pre);
    span({ class: "cas-nav-shortcut-ch" }, marked.ch);
    if (marked.post) span(marked.post);
  });
}

function renderSearchButton() {
  if (!NAVBAR.ctx || !NAVBAR.ctx.loggedIn) return;
  button(
    {
      type: "button",
      class: "cas-nav-search",
      title: "Search tools (Ctrl+K)",
      "aria-label": "Search tools",
      onclick: () => openPalette(),
    },
    () => {
      span({ class: "cas-nav-search-icon" }, () => icon("search"));
      span({ class: "cas-nav-search-label" }, "Search");
      kbd({ class: "cas-nav-search-kbd", "aria-hidden": "true" }, "Ctrl K");
    },
  );
}

function renderAccountMenu() {
  const ctx = NAVBAR.ctx;
  ul({ class: "navbar-nav cas-nav-account" }, () => {
    if (!ctx || !ctx.loggedIn) {
      // cas.js binds showLoginOverlay on #lLogin via cas:navbar-rendered.
      // hrefs here are empty, never javascript: URLs: those count as inline
      // script under the CSP (script-src without unsafe-inline) and clicking
      // them logs a blocked-navigation violation. The bound handlers
      // preventDefault, so the empty href never navigates either.
      li({ class: "nav-item" }, () => {
        a({ id: "lLogin", class: "nav-link cas-nav-signin", href: "" }, () => {
          span({ class: "cas-nav-topicon" }, () => icon("user"));
          span("Sign in");
        });
      });
      return;
    }
    li({ id: "account-menu-dropdown", class: "nav-item dropdown" }, () => {
      a(
        {
          id: "accountmenu",
          class: "nav-link dropdown-toggle cas-nav-acct-toggle",
          href: "",
          "data-bs-toggle": "dropdown",
          "aria-expanded": "false",
        },
        () => {
          span({ class: "cas-nav-avatar", "aria-hidden": "true" }, initials(ctx.fullName));
          span({ class: "cas-nav-acct-name" }, ctx.fullName || "Account");
        },
      );
      div({ class: "dropdown-menu dropdown-menu-end cas-nav-panel cas-nav-acct-panel" }, () => {
        div({ class: "cas-nav-acct-head" }, () => {
          span({ class: "cas-nav-acct-full" }, ctx.fullName || "Account");
          const role = roleLabel(ctx.adminLevel);
          if (role) span({ class: "cas-nav-acct-role" }, role);
        });
        a(
          { id: "lAccountSettings", class: "dropdown-item cas-nav-item", href: "/scs_bs/#account_settings" },
          () => {
            span({ class: "cas-nav-item-icon" }, () => icon("settings"));
            span("Account settings");
          },
        );
        a(
          { id: "lLogout", class: "dropdown-item cas-nav-item", href: "" },
          () => {
            span({ class: "cas-nav-item-icon" }, () => icon("arrowRight"));
            span("Logout");
          },
        );
      });
    });
  });
}

/** Up to two initials for the avatar pill ("Jane Q. Doe" -> "JD"). */
function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (first + last).toUpperCase() || "?";
}

/** Marks the top-level item owning the current page (white underline). */
function markActive() {
  const active = activeCommandFromHash(window.location.hash);
  for (const node of NAVBAR.topNodes) {
    node.el.classList.toggle("cas-nav-active", node.commands.has(active));
  }
}

// ---------------------------------------------------------------------------
// Render: the mobile offcanvas drawer
// ---------------------------------------------------------------------------

function renderOffcanvas() {
  const mount = GetElById("cas-nav-offcanvas-mount");
  if (!mount) return;
  NAVBAR.ocQuery = "";
  cdom.replaceInner(mount, () => {
    div(
      {
        id: "casNavOffcanvas",
        class: "offcanvas offcanvas-start cas-nav-offcanvas",
        tabindex: "-1",
        "aria-label": "Menu",
      },
      () => {
        div({ class: "offcanvas-header cas-nav-oc-header" }, () => {
          div({ class: "cas-nav-oc-title" }, "Menu");
          button({
            type: "button",
            class: "btn-close",
            "data-bs-dismiss": "offcanvas",
            "aria-label": "Close",
          });
        });
        div({ class: "offcanvas-body cas-nav-oc-body" }, () => {
          if (NAVBAR.tiles.length) {
            div({ class: "cas-nav-oc-search" }, () => {
              span({ class: "cas-nav-oc-search-icon" }, () => icon("search"));
              input({
                id: "casNavOcSearch",
                type: "text",
                class: "form-control",
                placeholder: "Search tools...",
                autocomplete: "off",
                "aria-label": "Search tools",
                oninput: (e) => {
                  NAVBAR.ocQuery = e.target.value || "";
                  const el = GetElById("casNavOcResults");
                  if (el) cdom.replaceInner(el, renderOffcanvasContent);
                },
              });
            });
          }
          div({ id: "casNavOcResults" }, renderOffcanvasContent);
        });
      },
    );
  });
}

function renderOffcanvasContent() {
  const query = (NAVBAR.ocQuery || "").trim();
  if (query) {
    const model = paletteResults({ tiles: NAVBAR.tiles, pins: [], recents: [], query });
    if (!model.results.length) {
      div({ class: "cas-nav-oc-empty" }, "No matches");
      return;
    }
    model.results.forEach((r) => renderOcLink(r.tile, r.breadcrumb));
    return;
  }

  const pinned = getPinnedKeys(loadPinsState())
    .map((k) => NAVBAR.byLaunchKey.get(k))
    .filter(Boolean);
  if (pinned.length) {
    div({ class: "cas-nav-oc-sec-title" }, () => {
      icon("pin", { class: "cas-nav-oc-sec-icon" });
      span("Pinned");
    });
    div({ class: "cas-nav-oc-pins" }, () => {
      pinned.forEach((tile) => {
        a({ class: "cas-nav-oc-pin", href: tile.href, title: tile.label }, () => {
          span({ class: "cas-nav-oc-pin-icon" }, () =>
            icon(GROUP_ICON[tile.groupKey || ""] || "layoutGrid"),
          );
          span(tile.label);
        });
      });
    });
  }

  // Bare top-level links (Home, Desktop, eBill, ...).
  div({ class: "cas-nav-oc-links" }, () => {
    NAVBAR.tree.filter((n) => n.type === "link").forEach((n) => renderOcLink(n.tile, ""));
  });

  // One accordion section per group; in-panel sections become subheaders.
  const groups = NAVBAR.tree.filter((n) => n.type === "group");
  if (!groups.length) return;
  div({ class: "accordion accordion-flush cas-nav-oc-accordion", id: "casNavOcAccordion" }, () => {
    groups.forEach((group, i) => {
      const bodyId = "casNavOcSec" + i;
      div({ class: "accordion-item" }, () => {
        div({ class: "accordion-header" }, () => {
          button(
            {
              type: "button",
              class: "accordion-button collapsed",
              "data-bs-toggle": "collapse",
              "data-bs-target": "#" + bodyId,
              "aria-expanded": "false",
              "aria-controls": bodyId,
            },
            () => {
              span({ class: "cas-nav-topicon" }, () =>
                icon(GROUP_ICON[group.key] || "layoutGrid"),
              );
              span(group.label);
            },
          );
        });
        div(
          {
            id: bodyId,
            class: "accordion-collapse collapse",
            "data-bs-parent": "#casNavOcAccordion",
          },
          () => {
            div({ class: "accordion-body cas-nav-oc-secbody" }, () => {
              group.items.forEach((tile) => renderOcLink(tile, ""));
              group.sections.forEach((section) => {
                div({ class: "cas-nav-oc-subtitle" }, section.label);
                section.items.forEach((tile) => renderOcLink(tile, ""));
              });
            });
          },
        );
      });
    });
  });
}

function renderOcLink(tile, breadcrumb) {
  a({ class: "cas-nav-oc-link", href: tile.href, title: tile.desc || tile.label }, () => {
    span({ class: "cas-nav-oc-link-label" }, tile.label);
    if (breadcrumb) span({ class: "cas-nav-oc-link-crumb" }, breadcrumb);
  });
}

// ---------------------------------------------------------------------------
// The command palette (Ctrl+K)
// ---------------------------------------------------------------------------

let paletteHost = null;

function ensurePaletteHost() {
  if (!paletteHost) {
    paletteHost = document.createElement("div");
    paletteHost.id = "casPaletteHost";
    document.body.appendChild(paletteHost);
  }
  return paletteHost;
}

function openPalette() {
  if (!NAVBAR.ctx || !NAVBAR.ctx.loggedIn || NAVBAR.palette) return;
  NAVBAR.palette = { query: "", selection: -1, model: null };
  renderPalette();
  const inp = GetElById("casPaletteInput");
  if (inp) inp.focus();
}

function closePalette() {
  if (!NAVBAR.palette) return;
  NAVBAR.palette = null;
  cdom.replaceInner(ensurePaletteHost(), () => {});
}

function renderPalette() {
  const host = ensurePaletteHost();
  cdom.replaceInner(host, () => {
    if (!NAVBAR.palette) return;
    div({ class: "cas-palette-backdrop", onclick: closePalette });
    div({ class: "cas-palette", role: "dialog", "aria-modal": "true", "aria-label": "Search tools" }, () => {
      div({ class: "cas-palette-inputwrap" }, () => {
        span({ class: "cas-palette-input-icon" }, () => icon("search"));
        input({
          id: "casPaletteInput",
          type: "text",
          class: "cas-palette-input",
          placeholder: "Search tools by name...",
          autocomplete: "off",
          "aria-label": "Search tools by name",
          oninput: (e) => {
            NAVBAR.palette.query = e.target.value || "";
            NAVBAR.palette.selection = -1;
            rerenderPaletteResults();
          },
          onkeydown: onPaletteKeydown,
        });
        kbd({ class: "cas-palette-esc", "aria-hidden": "true" }, "esc");
      });
      div({ id: "casPaletteResults", class: "cas-palette-results" }, renderPaletteResults);
      // Browse mode (an empty box lists Favorites and Recent) and the arrow/Enter
      // model are both invisible to anyone who starts typing straight away, which
      // is what the palette invites you to do. The tip sits in a footer rather
      // than under the input so it never pushes results down, and it carries no
      // hardcoded fallback: nothing stands here today, so an unresolved tag
      // rendering nothing loses nothing, and helpText still warns to the console.
      const tip = helpText("NAV_PALETTE_HINT");
      if (tip) div({ class: "cas-palette-foot" }, tip);
    });
  });
}

function rerenderPaletteResults() {
  const el = GetElById("casPaletteResults");
  if (el) cdom.replaceInner(el, renderPaletteResults);
}

function currentPaletteModel() {
  // The palette's Favorites section mirrors the homepage quick-action bar
  // exactly, via the same selection function: the user's pins when they have
  // any, the role's default quick actions otherwise. Sharing the selector is
  // what keeps the two surfaces in sync by construction.
  const { tiles: favorites } = quickLaunchTiles(
    NAVBAR.tiles,
    getPinnedKeys(loadPinsState()),
    quickActionCommandsForRole(NAVBAR.ctx ? NAVBAR.ctx.adminLevel : -1),
    4,
  );
  return paletteResults({
    tiles: NAVBAR.tiles,
    pins: favorites.map(launchKey),
    recents: getRecentLaunchEntries(loadRecentsState()),
    query: NAVBAR.palette ? NAVBAR.palette.query : "",
  });
}

function renderPaletteResults() {
  const p = NAVBAR.palette;
  if (!p) return;
  const model = (p.model = currentPaletteModel());
  let index = 0;
  const row = (tile, extra) => renderPaletteRow(tile, extra, index++ === p.selection);

  if (model.mode === "browse") {
    if (model.pinned.length) {
      paletteSectionTitle("Favorites", "pin");
      model.pinned.forEach((tile) => row(tile, {}));
    }
    if (model.recent.length) {
      paletteSectionTitle("Recent", "history");
      model.recent.forEach((r) => row(r.tile, { ts: r.ts }));
    }
    if (!model.pinned.length && !model.recent.length) {
      // Authored copy, with the short fallback pattern the homepage pin tip uses:
      // this line exists today, so an unresolved tag must not blank it, and the
      // console warning is still the only trace of a JS-ahead-of-copy deploy.
      // The tool count the old string interpolated is gone on purpose: webhelp
      // copy is a flat string with no placeholder convention, and a translator
      // cannot move a number this code splices around. Nothing acted on it.
      div(
        { class: "cas-palette-hint" },
        helpText("NAV_PALETTE_EMPTY") ||
          "Type to search, then use the pin icon on a result to keep it here.",
      );
    }
    return;
  }
  if (!model.results.length) {
    div({ class: "cas-palette-hint" }, 'No matches for "' + p.query.trim() + '"');
    return;
  }
  model.results.forEach((r) => row(r.tile, { breadcrumb: r.breadcrumb }));
}

function paletteSectionTitle(title, iconName) {
  div({ class: "cas-palette-sec" }, () => {
    icon(iconName, { class: "cas-palette-sec-icon" });
    span(title);
  });
}

function renderPaletteRow(tile, extra, selected) {
  const pinnedNow = isPinned(loadPinsState(), launchKey(tile));
  div({ class: "cas-palette-row" + (selected ? " cas-palette-row--active" : "") }, () => {
    a({ class: "cas-palette-row-link", href: tile.href, title: tile.label, onclick: closePalette }, () => {
      span({ class: "cas-palette-row-icon" }, () =>
        icon(GROUP_ICON[tile.groupKey || ""] || "layoutGrid"),
      );
      span({ class: "cas-palette-row-text" }, () => {
        span({ class: "cas-palette-row-label" }, tile.label);
        const crumb = extra.breadcrumb || tile.group || "";
        if (crumb) span({ class: "cas-palette-row-crumb" }, crumb);
      });
      if (extra.ts) span({ class: "cas-palette-row-time" }, formatRelativeTime(extra.ts));
    });
    button(
      {
        type: "button",
        class: "cas-palette-row-pin" + (pinnedNow ? " cas-palette-row-pin--on" : ""),
        title: pinnedNow ? "Unpin " + tile.label : "Pin " + tile.label,
        "aria-label": pinnedNow ? "Unpin " + tile.label : "Pin " + tile.label,
        "aria-pressed": pinnedNow ? "true" : "false",
        onclick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          // The palette can open before the per-login state has loaded, and a
          // toggle over an empty pin list would save {entries:[thisOne]} over
          // the user's whole list. Read the pins only once the state is ready.
          userState.ready().then(() => {
            savePinsState(togglePinned(loadPinsState(), launchKey(tile)));
            // The homepage launchpad renders its favorites bar and tile pin states
            // from an in-memory copy loaded once, so it cannot see this write on
            // its own. Announce it; a homepage behind the palette re-reads.
            document.dispatchEvent(new CustomEvent(PINNED_LAUNCH_EVENT));
            rerenderPaletteResults();
          });
        },
      },
      () => icon("pin"),
    );
  });
}

function onPaletteKeydown(e) {
  const p = NAVBAR.palette;
  if (!p) return;
  if (e.key === "Escape") {
    e.preventDefault();
    closePalette();
    return;
  }
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    const flat = flattenResults(p.model || currentPaletteModel());
    p.selection = moveSelection(p.selection, e.key === "ArrowDown" ? 1 : -1, flat.length);
    // The results box scrolls (max-height 52vh over a catalog of dozens) and
    // moving the selection never moved the box, so arrowing past the eighth row
    // highlighted something off screen and Enter opened a tool the user could
    // not see. Only worth noticing now because the new footer tells people to
    // drive the palette this way.
    //
    // The scroll offset has to be carried across the re-render by hand: cdom's
    // replaceInner empties the element (textContent = "") before refilling it,
    // which collapses its scrollHeight and makes the browser clamp scrollTop to
    // 0. Without the restore, scrollIntoView re-anchors from the top and an
    // ArrowUp of one row appears to jump backwards by most of a page.
    const box = GetElById("casPaletteResults");
    const keepTop = box ? box.scrollTop : 0;
    rerenderPaletteResults();
    if (box) box.scrollTop = keepTop;
    const row = box && box.querySelector(".cas-palette-row--active");
    if (row) row.scrollIntoView({ block: "nearest" });
    return;
  }
  if (e.key === "Enter") {
    e.preventDefault();
    const flat = flattenResults(p.model || currentPaletteModel());
    const tile = p.selection >= 0 ? flat[p.selection] : flat[0];
    if (tile) launchTile(tile);
  }
}

// ---------------------------------------------------------------------------
// Navigation / keyboard plumbing
// ---------------------------------------------------------------------------

/** Navigates to a tile. Hash hrefs ride the SPA's hashchange pipeline (which
 *  also records the launch in the shared recents); legacy CGI hrefs do a full
 *  page navigation, exactly like clicking the link. */
function launchTile(tile) {
  closePalette();
  window.location.href = tile.href;
}

function groupLi(groupKey) {
  const mount = GetElById("cas-navbar-mount");
  if (!mount) return null;
  for (const el of mount.querySelectorAll("li.cas-nav-group")) {
    if (el.getAttribute("data-group-key") === groupKey) return el;
  }
  return null;
}

function closeOpenDropdowns() {
  if (!window.bootstrap || !window.bootstrap.Dropdown) return;
  document.querySelectorAll(".cas-navbar .dropdown-toggle.show").forEach((toggle) => {
    const inst = window.bootstrap.Dropdown.getInstance(toggle);
    if (inst) inst.hide();
  });
}

function clearArmedSections() {
  document
    .querySelectorAll(".cas-nav-section--armed")
    .forEach((el) => el.classList.remove("cas-nav-section--armed"));
}

function applyShortcutAction(action, evt) {
  if (!action || action.type === "none") return;
  evt.preventDefault();
  switch (action.type) {
    case "launch":
      closeOpenDropdowns();
      launchTile(action.tile);
      return;
    case "open-group": {
      const liEl = groupLi(action.group.key);
      const toggle = liEl && liEl.querySelector(".dropdown-toggle");
      if (toggle && window.bootstrap && window.bootstrap.Dropdown) {
        window.bootstrap.Dropdown.getOrCreateInstance(toggle).show();
      }
      return;
    }
    case "arm-section": {
      const liEl = groupLi(action.group.key);
      if (!liEl) return;
      liEl.querySelectorAll(".cas-nav-section").forEach((el) => {
        el.classList.toggle(
          "cas-nav-section--armed",
          el.getAttribute("data-section-key") === action.section.key,
        );
      });
      return;
    }
    case "disarm":
      clearArmedSections();
      return;
    case "close":
      clearArmedSections();
      closeOpenDropdowns();
      return;
  }
}

function onGlobalKeydown(e) {
  // Ctrl+K / Cmd+K: the palette, from anywhere (including inside inputs).
  if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === "k" || e.key === "K")) {
    if (NAVBAR.ctx && NAVBAR.ctx.loggedIn) {
      e.preventDefault();
      openPalette();
    }
    return;
  }
  // Menu shortcut letters: only with the pref on and nothing else focused.
  // "Nothing else" means the body, like the old cas.js listener, OR the
  // navbar itself: Bootstrap's Dropdown.show() focuses the toggle button on
  // open, so the second keystroke of a sequence (C then D) arrives with the
  // toggle focused and must still count.
  if (!NAVBAR.ctx || !NAVBAR.ctx.useKeyboardShortcuts) return;
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  const ae = document.activeElement;
  const inNavbar = ae && ae.closest && ae.closest("#cas-navbar") && ae.nodeName !== "INPUT";
  if (!(!ae || ae.nodeName === "BODY" || inNavbar)) return;
  const result = step(NAVBAR.shortcutState, e.key, NAVBAR.tree);
  NAVBAR.shortcutState = result.state;
  applyShortcutAction(result.action, e);
}

/** Keeps the shortcut machine in sync when menus open/close by mouse.
 *  Bootstrap dropdown events fire on the toggle and bubble. */
function onDropdownShown(e) {
  const liEl = e.target && e.target.closest ? e.target.closest("li.cas-nav-group") : null;
  if (!liEl) return;
  NAVBAR.shortcutState = { level: "group", groupKey: liEl.getAttribute("data-group-key") };
  // Wide (mega) panels anchored at the toggle's left edge can run past the
  // viewport; flip them to right-anchored when they would.
  const panel = liEl.querySelector(".cas-nav-panel");
  if (panel) {
    panel.classList.remove("cas-nav-panel--flip");
    const rect = panel.getBoundingClientRect();
    if (rect.right > window.innerWidth - 8) {
      panel.classList.add("cas-nav-panel--flip");
    }
  }
}

function onDropdownHidden(e) {
  const liEl = e.target && e.target.closest ? e.target.closest("li.cas-nav-group") : null;
  if (!liEl) return;
  clearArmedSections();
  const state = NAVBAR.shortcutState;
  if (state.level !== "idle" && state.groupKey === liEl.getAttribute("data-group-key")) {
    NAVBAR.shortcutState = IDLE_STATE;
  }
}

function closeOffcanvas() {
  const oc = GetElById("casNavOffcanvas");
  if (oc && window.bootstrap && window.bootstrap.Offcanvas) {
    const inst = window.bootstrap.Offcanvas.getInstance(oc);
    if (inst) inst.hide();
  }
}

function onHashChange() {
  markActive();
  closePalette();
  closeOffcanvas();
}

/**
 * Re-clicking the link for the page you are already on: the hash would not
 * change, so no hashchange fires and the click silently does nothing. Catch
 * that case on the nav surfaces and force a reload through NavigateTo's
 * refresh path instead (which also closes the login overlay on success).
 */
function onNavSurfaceClick(e) {
  const target = e.target;
  if (!target || !target.closest) return;
  if (!target.closest("#cas-navbar, #cas-nav-offcanvas-mount, #casPaletteHost")) return;
  const link = target.closest("a[href]");
  if (!link) return;
  const href = link.getAttribute("href") || "";
  const hashIdx = href.indexOf("#");
  if (hashIdx === -1) return;
  // Only same-document SPA links: an absolute path must be the SPA's own.
  const path = href.slice(0, hashIdx);
  if (path && path !== window.location.pathname) return;
  const hash = href.slice(hashIdx);
  if (!hash || hash === "#" || hash !== window.location.hash) return;
  e.preventDefault();
  closePalette();
  closeOffcanvas();
  closeOpenDropdowns();
  if (typeof window.NavigateTo === "function") {
    window.NavigateTo(hash, true);
  }
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

const mountEl = GetElById("cas-navbar-mount");
if (mountEl) {
  NAVBAR.catalog = readLaunchCatalog();

  document.addEventListener("cas:security-updated", (e) => {
    setContext(e.detail);
    render();
    // user_state.js listens to the same event and (re)fetches; its ready()
    // resolves when that fetch settles (or at its hard cap, serving the
    // mirror), so this never leaves the bar waiting forever.
    userState.ready().then(onUserStateReady);
  });
  // The mobile drawer's Pinned section is built from storage once per
  // cas:security-updated, i.e. at boot and after logout, and never on open. So
  // a pin changed anywhere else (this palette, the homepage tile grid) left it
  // listing a favorite the user had already removed, for the rest of the
  // session. This module loads once with the shell, so a plain listener here
  // cannot accumulate the way a page module's would.
  document.addEventListener(PINNED_LAUNCH_EVENT, () => {
    const el = GetElById("casNavOcResults");
    if (el) cdom.replaceInner(el, renderOffcanvasContent);
  });
  document.addEventListener("keydown", onGlobalKeydown);
  document.addEventListener("click", onNavSurfaceClick);
  document.addEventListener("shown.bs.dropdown", onDropdownShown);
  document.addEventListener("hidden.bs.dropdown", onDropdownHidden);
  window.addEventListener("hashchange", onHashChange);

  // Belt and braces: if cas.js somehow finished its security round trip before
  // this module evaluated, render from the globals instead of waiting.
  if (window.SECURITY_PARAMS && window.SECURITY_PARAMS.Response && Array.isArray(window.APPS_STATUS)) {
    setContext({ securityParams: window.SECURITY_PARAMS, webApps: window.APPS_STATUS });
    render();
    // No event is coming for this round trip; ready() reads the same global.
    userState.ready().then(onUserStateReady);
  } else {
    render();
  }
}
